/*
 * *
 *   ~  * Copyright (c) by INFODATA-GROUP. All rights reserved.
 *   ~  * No part of this publication may be reproduced, distributed,
 *   ~  * or transmitted in any form or by any means, including photocopying, recording,
 *   ~  * or other electronic or mechanical methods, without the prior written permission of the publisher,
 *   ~  * except in the case of brief quotations embodied in critical
 *   ~  * reviews and certain other noncommercial uses permitted by copyright law.
 *   ~  * AUTHOR : bs
 *   ~  * DATE : 12/05/2025 15:11
 *
 */

import { Injectable } from '@angular/core';

export interface DeviceState {
  isDesktop: boolean;
  desktopOS: DesktopOS | undefined;
  isWindowsDesktop: boolean;
  isLinuxOrUnixDesktop: boolean;

  isMobile: boolean;
  mobileOS: MobileOS | undefined;
  isAndroidDevice: boolean;
  isAppleDevice: boolean;
  isUnknownMobileDevice: boolean;

  isTablet: boolean;
  isLandscapeOrientation(): boolean;
  isPortraitOrientation(): boolean;
}

enum MobileOS {
  Android = 'android',
  iOS = 'ios',
  Unknown = 'unknown',
  WindowsPhone = 'Windows Phone',
}

enum DesktopOS {
  Linux = 'linux',
  MacOS = 'mac_os',
  Unix = 'unix',
  Unknown = 'unknown',
  Windows = 'windows',
}

type DeviceOS = MobileOS | DesktopOS;

@Injectable({
  providedIn: 'root',
})
export class DeviceDetectorService {
  private userAgent: string =
    navigator.userAgent || (window as any).opera || undefined;

  public isMobileDevice(): boolean {
    const regex = [
      /(Android)(.+)(Mobile)/i,
      /BlackBerry/i,
      /iPhone|iPod|iPad/i,
      /Opera Mini/i,
      /IEMobile/i,
    ];
    return regex.some((b) => this.userAgent.match(b));
  }

  public getMobileOS(): MobileOS | undefined {
    if (this.isMobileDevice()) {
      if (/windows phone/i.test(this.userAgent)) return MobileOS.WindowsPhone;
      else if (/android/i.test(this.userAgent)) return MobileOS.Android;
      else if (
        /iPad|iPhone|iPod/.test(this.userAgent) &&
        !(window as any).MSStream
      )
        return MobileOS.iOS;

      return MobileOS.Unknown;
    } else return undefined;
  }

  public isTabletDevice(): boolean {
    const regex =
      /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/;
    return regex.test(this.userAgent.toLowerCase());
  }

  public isDesktopDevice(): boolean {
    return !this.isMobileDevice() && !this.isTabletDevice();
  }

  public getDesktopOS(): DesktopOS | undefined {
    if (this.isDesktopDevice()) {
      if (this.userAgent.indexOf('Win') !== -1) return DesktopOS.Windows;
      else if (this.userAgent.indexOf('Mac') !== -1) return DesktopOS.MacOS;
      else if (this.userAgent.indexOf('X11') !== -1) return DesktopOS.Unix;
      else if (this.userAgent.indexOf('Linux') !== -1) return DesktopOS.Linux;

      return DesktopOS.Unknown;
    } else return undefined;
  }

  public getDeviceOS(): DeviceOS | undefined {
    return this.getMobileOS() ?? this.getDesktopOS();
  }
}
